import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import deleteIcon from '../../asset/icon/delete.png';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import {
  SearchMessage,
  SearchResults,
} from '../../shared/component/modal/SearchModalLayout';
import { ChangeModalFrame } from '../../shared/component/modal/ChangeModalFrame';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { getSettingsMessage } from '../../shared/constants/pageMessages';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { useLinkedGroupMember } from '../../shared/hooks/useApi/useLinkedGroupMember';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';

interface LinkedGroupMemberManagementModalProps {
  setIsUserInput?: (isUserInput: boolean) => void;
}

const LinkedGroupMemberManagementModal: React.FC<LinkedGroupMemberManagementModalProps> = ({
  setIsUserInput,
}) => {
  const { connections, status, unlink, getConnections } = useLinkedGroupMember();
  const { isClosing, clearModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();

  useEffect(() => {
    setIsUserInput?.(false);
    return () => setIsUserInput?.(false);
  }, [setIsUserInput]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearModal]);

  const handleClose = () => clearModal();

  const handleUnlink = (groupMemberId: string, groupName: string) => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_UNLINK_LINKED_GROUP_MEMBER', { groupName }),
      callBack: async () => {
        await unlink(groupMemberId);
      },
    });
  };

  const list = (
    <ConnectionList>
        {(status === 'idle' || status === 'loading') && (
          <SearchMessage>{getSettingsMessage('CONNECTION_LOADING')}</SearchMessage>
        )}
        {status === 'error' && (
          <ErrorMessage role="alert">
            <span>{getSettingsMessage('CONNECTION_FAILED')}</span>
            <RowButton onClick={() => void getConnections()}>
              {getBtnName('RETRY')}
            </RowButton>
          </ErrorMessage>
        )}
        {status === 'success' && connections.length === 0 && (
          <SearchMessage>{getSettingsMessage('CONNECTION_NONE')}</SearchMessage>
        )}
        {connections.map(({ groupAui, groupName, groupHandle, groupMember }) => (
          <ConnectionItem key={`${groupAui}-${groupMember.id}`}>
            <ConnectionInfo>
              <GroupArchiveLink
                to={`/@${groupHandle}/`}
                onClick={handleClose}
              >
                {groupName}
              </GroupArchiveLink>
              <ConnectionMeta>@{groupHandle}</ConnectionMeta>
            </ConnectionInfo>
            <ActionWrap>
              <HeadlessBtn
                value={getBtnName('UNLINK')}
                handleClick={() => handleUnlink(groupMember.id, groupName.trim() || groupHandle)}
                StyledBtn={RowButton}
              />
            </ActionWrap>
          </ConnectionItem>
        ))}
      </ConnectionList>
  );

  return (
    <ChangeModalFrame
      $isClosing={isClosing}
      onClick={(event) => event.stopPropagation()}
    >
      <DesktopBody>
        <HeaderRow>
          <DesktopTitle>{getSettingsMessage('SETTINGS_CONNECTIONS')}</DesktopTitle>
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            hoverOpacity
            backgroundImg={deleteIcon}
            onClick={handleClose}
            aria-label="Close"
          />
        </HeaderRow>
        {list}
      </DesktopBody>
    </ChangeModalFrame>
  );
};

export default LinkedGroupMemberManagementModal;

const DesktopBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0.5vw;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3vh;
`;

const DesktopTitle = styled.h2`
  min-width: 0;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ConnectionList = styled(SearchResults)``;

const ConnectionItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  box-sizing: border-box;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
`;

const ConnectionInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupArchiveLink = styled(Link)`
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  text-decoration: underline;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const ConnectionMeta = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const ActionWrap = styled.div`
  flex-shrink: 0;
`;

const ErrorMessage = styled(SearchMessage)`
  flex-direction: column;
  gap: 10px;
`;

const RowButton = styled(PaperBlurFitBtn)`
  padding: 6px 10px;
`;
